/* Dr. Johnny's — Cart cross-sell "complete the routine" (CA). CORRECTED 2026-09-16.
   Client-side render via /products/{handle}?section_id=product-json-data.
   Hooks theme:cart::added / removed + storage. Same DOM classes as before.

   WHAT CHANGED vs v2 (why the card was blank):
   - v2 recommended DRAFT 3-pack bundles + a diabetic single handle that doesn't exist
     ('diabetic-socks-solid-black-ftd710c91' -> 404 -> null -> card dropped).
   - We've retired pre-made 3-packs for the build-your-own ladder, so this now
     recommends the Foot Cream (the true "routine" complement) + one live hero single.
   - Every handle below is verified ACTIVE on drjohnnys.ca (2026-09-16).
*/
class CartCrossSell extends HTMLElement {
  constructor() {
    super();
    this.handles = {
      cream:    'dr-johnny-s-foot-cream',            // ACTIVE
      diabetic: 'diabetic-socks-solid-black',        // ACTIVE (was a dead handle in v2)
      comp:     'solid-cotton-black-energy-socks'     // ACTIVE
    };
    this._productCache = {};
    this._rendering = false;

    document.addEventListener('theme:cart::added',   () => this._debouncedInit());
    document.addEventListener('theme:cart::removed', () => this._debouncedInit());
    window.addEventListener('storage', (e) => {
      if (e.key === 'cartCurrentData') this._debouncedInit();
    });

    this.init();
  }

  _debouncedInit() {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this.init(), 300);
  }

  async init() {
    if (this._rendering) return;
    this._rendering = true;

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const wanted = this.determineHandles(cart);   // array of up to 2 handles

      if (!wanted.length) { this.innerHTML = ''; return; }

      const products = await Promise.all(wanted.map(h => this._fetchProduct(h)));
      this.innerHTML = this._renderModule(products.filter(Boolean));
    } catch (err) {
      console.error('[CartCrossSell] init error:', err);
    } finally {
      this._rendering = false;
    }
  }

  async _fetchProduct(handle) {
    if (!handle) return null;
    if (this._productCache[handle]) return this._productCache[handle];
    try {
      const res = await fetch(`/products/${handle}?section_id=product-json-data`);
      if (!res.ok) return null;
      const htmlText = await res.text();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlText;
      const data = JSON.parse(tempDiv.textContent.trim());
      this._productCache[handle] = data;
      return data;
    } catch (err) {
      console.error(`[CartCrossSell] Failed to fetch ${handle}:`, err);
      return null;
    }
  }

  /* Decide up to two complements, never anything already in the cart.
     Card 1 = Foot Cream (the routine anchor). Card 2 = a complementary sock single. */
  determineHandles(cart) {
    const inCart = (h) => cart.items.some(i => i.handle === h);

    // Tally category among socks already in the cart
    let diabQty = 0, compQty = 0;
    cart.items.forEach(i => {
      if (i.handle === this.handles.cream) return;
      if (i.handle.includes('diabetic')) diabQty += i.quantity;
      else if (i.handle.includes('compression') || i.handle.includes('energy-socks')) compQty += i.quantity;
    });

    // Complementary single: opposite of what dominates the cart; default compression
    let sockHandle;
    if (compQty > diabQty)      sockHandle = this.handles.diabetic;
    else if (diabQty > compQty) sockHandle = this.handles.comp;
    else                        sockHandle = this.handles.comp; // cream-only or tie -> hero compression

    const picks = [];
    if (!inCart(this.handles.cream)) picks.push(this.handles.cream);
    if (!inCart(sockHandle))         picks.push(sockHandle);

    // If cream is already in the cart, offer BOTH hero singles they don't yet have
    if (inCart(this.handles.cream)) {
      [this.handles.comp, this.handles.diabetic].forEach(h => {
        if (!inCart(h) && !picks.includes(h)) picks.push(h);
      });
    }

    return picks.slice(0, 2);
  }

  _renderModule(products) {
    if (!products.length) return '';
    const cardsHtml = products.map(p => this._renderCard(p)).join('');
    return `
      <div class="cross-sell-routine">
        <div class="cross-sell-routine__header">
          <h2 class="h3">The complete routine</h2>
          <p>Apply the cream at night. Wear the socks all day. Together, they go further.</p>
        </div>
        <div class="cross-sell-routine__grid">
          ${cardsHtml}
        </div>
      </div>
    `;
  }

  _renderCard(product) {
    const rawImg = product.featured_image || (product.images && product.images[0]) || '';
    const imgSrc = rawImg ? rawImg.replace(/\.(png|jpg|jpeg|webp|gif)/i, '_600x.$1') : '';
    const url = `/products/${product.handle}`;
    const price = this._formatMoney(product.variants?.[0]?.price);
    const availableVariants = (product.variants || []).filter(v => v.available);

    const optionsHtml = availableVariants.map(v =>
      `<option value="${v.id}" data-value="${v.id}">${this._escapeHtml(v.title)}</option>`
    ).join('');
    const hasMultipleVariants = availableVariants.length > 1;

    const selectBlock = hasMultipleVariants ? `
      <div class="cross-sell-select-wrapper">
        <select name="id" class="cross-sell-card__select">
          ${optionsHtml}
        </select>
      </div>
    ` : `<input type="hidden" name="id" value="${availableVariants[0]?.id || ''}">`;

    return `
      <div class="cross-sell-card" data-js-product>
        <div class="cross-sell-card__image">
          <a href="${url}"><img src="${imgSrc}" alt="${this._escapeHtml(product.title)}" loading="lazy"></a>
        </div>
        <div class="cross-sell-card__info">
          <h3 class="h5"><a href="${url}">${this._escapeHtml(product.title)}</a></h3>
          <div class="price">${price}</div>
        </div>
        <form method="post" action="/cart/add" class="cross-sell-card__form js-product-form"
              accept-charset="UTF-8" enctype="multipart/form-data" novalidate="novalidate"
              data-type="add-to-cart-form" data-js-product-form>
          <input type="hidden" name="form_type" value="product">
          <input type="hidden" name="utf8" value="✓">
          ${selectBlock}
          <button type="submit"
                  class="btn btn--full btn--status js-add-to-cart js-product-button-add-to-cart"
                  name="add" data-js-product-button-add-to-cart>
            <span class="d-flex flex-center"><span class="btn__text">Add to Cart</span></span>
          </button>
        </form>
      </div>
    `;
  }

  _formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    if (window.Shopify?.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  _escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }
}
customElements.define('cart-cross-sell', CartCrossSell);